import Heading from '@collinsonx/design-system/components/heading/Heading';
import { Button, Center, Flex, Stack } from '@collinsonx/design-system/core';
import { useForm } from '@collinsonx/design-system/form';
import { FlightDetails, LinkedAccount } from '@collinsonx/utils';
import {
  ApolloError,
  useLazyQuery,
  useMutation,
} from '@collinsonx/utils/apollo';
import linkAccount from '@collinsonx/utils/mutations/linkAccount';
import { getFlightDetails } from '@collinsonx/utils/queries';
import BookingAmendmentPayInFullInfoMessage from '@components/booking/BookingAmendmentPayInFullInfoMessage';
import DomNode from '@components/DomNode';
import EntitlementsBlock from '@components/EntitlementsBlock/EntitlementsBlock';
import { FlightInfo } from '@components/flightInfo/FlightInfo';
import GuestInfo from '@components/GuestInfo';
import Layout from '@components/Layout';
import LoungeInfo from '@components/LoungeInfo';
import TopBarLinks from '@components/TopBarLinks';
import TotalPriceBlock from '@components/TotalPriceBlock/TotalPriceBlock';
import { accountIsEqual, getItem } from '@lib';
import {
  BOOKING_MODE,
  CODE_IATA,
  CODE_ICAO,
  DATE_FORMAT,
  DATE_TIME_FLIGHT_FORMAT,
  OAG_API_VERSION,
} from 'config/constants';
import { ANALYTICS_TAGS, PAGENAMES } from 'config/datadog';
import { FlightTimeRegEx } from 'config/flights';
import { BOOKING_MODE_STATE } from 'config/storage';
import { BookingContext } from 'context/BookingContext';
import { FlightContext } from 'context/FlightContext';
import dayjs from 'dayjs';
import useAnalytics from 'hooks/useAnalytics';
import useLocale from 'hooks/useLocale';
import usePayload from 'hooks/usePayload';
import useProduct from 'hooks/useProduct';
import useScrollIntoView from 'hooks/useScrollTo';
import { debounce } from 'lodash';
import router from 'next/router';
import { useCallback, useContext, useEffect, useState } from 'react';
import { formatDate } from 'utils/DateFormatter';

import enrolLinkedAccountToAvailablePlans from '../../gql-queries/enrolLinkedAccountToAvailablePlans';
import { validateFlightNumber } from '../../utils/flightValidation';
import classes from './CheckAvailability.module.css';

interface CheckAvailabilityProps {
  pageName: PAGENAMES;
}

const CheckAvailability = ({ pageName }: CheckAvailabilityProps) => {
  const [guestError, setGuestError] = useState<boolean>(false);
  const [checkAvailabilityLoading, setCheckAvailabilityLoading] =
    useState<boolean>(false);
  const [noFlightInfoError, setNoFlightInfoError] = useState<boolean>(false);
  const { consumerData, jwt, payload, referrerUrl } = usePayload();
  const { category: productCategory } = useProduct();
  const { booking, setBooking } = useContext(BookingContext);
  const { setFlight } = useContext(FlightContext);
  const { logAction } = useAnalytics();
  const [flightInfoRef, scrollToFlightInfo] = useScrollIntoView();
  const { airportCode, loadingProduct, maxPartySize, viewOnly } = useProduct();

  const {
    adults,
    children,
    currentPrice,
    departureDate,
    existing_booking_slot,
    flightNumber,
    flightTime,
    infants,
    reference,
    seniors,
    taxableGuestsCount,
  } = booking;

  const translations = useLocale();
  const mode = getItem(BOOKING_MODE_STATE);

  const findLinkedAccount = useCallback(
    (linkedAccounts: LinkedAccount[] = []) =>
      linkedAccounts.find(accountIsEqual(payload)),
    [payload]
  );

  const [dolinkAccount] = useMutation(linkAccount);
  const [enrolToAvailablePlans] = useMutation(
    enrolLinkedAccountToAvailablePlans
  );

  const handleLinkAccount = useCallback(
    (email: string, token: string) =>
      dolinkAccount({
        variables: {
          linkedAccountInput: {
            analytics: { email },
            token,
          },
        },
      }),
    []
  );

  useEffect(() => {
    if (consumerData) {
      const consumer = consumerData.getConsumerByID;
      const { linkedAccounts } = consumer;

      if (consumerData?.getConsumerByID.emailAddress !== payload?.email || !jwt)
        return;

      const matchedAccount = findLinkedAccount(linkedAccounts || []);

      if (
        payload?.membershipType !== matchedAccount?.membershipType ||
        payload?.sourceCode !== matchedAccount?.sourceCode
      ) {
        handleLinkAccount(consumerData?.getConsumerByID.emailAddress, jwt);
      }

      if (
        (!matchedAccount?.memberships ||
          matchedAccount?.memberships.length === 0) &&
        matchedAccount?.sourceCode !== undefined
      ) {
        enrolToAvailablePlans({
          variables: {
            linkedAccountID: matchedAccount?.id,
          },
        });
      }
    }
  }, [consumerData, handleLinkAccount, findLinkedAccount]);

  const form = useForm({
    initialValues: {
      adults,
      children,
      currentPrice,
      departureDate,
      existing_booking_slot,
      flightNumber,
      flightTime,
      infants,
      reference,
      seniors,
    },
    onValuesChange: debounce(
      ({
        adults,
        children,
        departureDate,
        flightNumber,
        flightTime,
        infants,
        seniors,
      }) => {
        setBooking({
          adults,
          children,
          departureDate,
          flightNumber,
          flightTime,
          infants,
          seniors,
        });
      },
      50
    ),
    transformValues: (values) => ({
      ...values,
      flightNumber: values.flightNumber.toUpperCase(),
    }),
    validate: {
      departureDate: (value: Date | null) => {
        logAction({
          action:
            mode === BOOKING_MODE.CREATE
              ? ANALYTICS_TAGS.ON_CHANGE_DATE_ERROR
              : ANALYTICS_TAGS.ON_CHANGE_DATE_ERROR_EDIT,
          page: pageName,
        });

        const error = value
          ? null
          : translations.booking.flightDetails.errors.invalid_date;

        if (error) scrollToFlightInfo();

        return error;
      },
      flightNumber: (value: string) => {
        let error = null;

        const validFlight =
          /^([A-Za-z]{2}|[A-Za-z]{3}|[A-Za-z][0-9]|[0-9][A-Za-z])([0-9]+[A-Za-z]?)$/.test(
            value.toUpperCase()
          );

        if (!validFlight) {
          error = translations.booking.flightDetails.errors.invalid_flight;
          logAction({
            action:
              mode === BOOKING_MODE.CREATE
                ? ANALYTICS_TAGS.ON_CHANGE_FLIGHT_NUMBER_ERROR
                : ANALYTICS_TAGS.ON_CHANGE_FLIGHT_NUMBER_ERROR_EDIT,
            page: pageName,
          });
        }

        if (error) scrollToFlightInfo();

        return error;
      },
      flightTime: (value: string) => {
        let error = null;

        const validTime = FlightTimeRegEx.test(value);

        if (!validTime && noFlightInfoError) {
          error = translations.booking.flightDetails.errors.invalid_flight;
        }

        return error;
      },
    },
  });

  type FormValues = typeof form.values;

  const [fetchFlightInfo] = useLazyQuery<{
    getFlightDetails: FlightDetails[];
  }>(getFlightDetails, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (flightInfoData) => {
      if (flightInfoData.getFlightDetails.length === 0) {
        setNoFlightInfoError(true);

        logAction({
          action:
            mode === BOOKING_MODE.CREATE
              ? ANALYTICS_TAGS.ON_CHANGE_FLIGHT_NUMBER_ERROR
              : ANALYTICS_TAGS.ON_CHANGE_FLIGHT_NUMBER_ERROR_EDIT,
          page: pageName,
        });
      } else if (form.isValid()) {
        setFlight(flightInfoData.getFlightDetails[0]);
        const { query } = router;
        router.push({
          pathname: '/check-slots',
          query: {
            ...query,
          },
        });
      }
      setCheckAvailabilityLoading(false);
    },
    onError: (error: ApolloError) => {
      setNoFlightInfoError(true);

      logAction({
        action:
          mode === BOOKING_MODE.CREATE
            ? ANALYTICS_TAGS.ON_CHANGE_FLIGHT_NUMBER_ERROR
            : ANALYTICS_TAGS.ON_CHANGE_FLIGHT_NUMBER_ERROR_EDIT,
        data: {
          graphQLError: error,
        },
        page: pageName,
      });
      setCheckAvailabilityLoading(false);
    },
    pollInterval: 300000,
  });

  const handleClickCheckAvailability = async (values: FormValues) => {
    logAction({
      action:
        mode === BOOKING_MODE.CREATE
          ? ANALYTICS_TAGS.ON_CONTINUE_BUTTON_AVI
          : ANALYTICS_TAGS.ON_CONTINUE_BUTTON_AVI_EDIT,
      page: pageName,
    });

    if (taxableGuestsCount > maxPartySize) {
      setGuestError(true);
      logAction({
        action:
          mode === BOOKING_MODE.CREATE
            ? ANALYTICS_TAGS.ON_CHANGE_ERROR_ATTENDEES_AVL
            : ANALYTICS_TAGS.ON_CHANGE_ERROR_ATTENDEES_AVL_EDIT,
        page: pageName,
      });
      return false;
    }
    setGuestError(false);

    if (form.isValid()) {
      setCheckAvailabilityLoading(true);
      const upperCaseFlight = form.values.flightNumber.toUpperCase();
      const carrieCode = validateFlightNumber(upperCaseFlight)[1];
      const flightNo = validateFlightNumber(upperCaseFlight)[2];

      if (noFlightInfoError || booking?.flightTime) {
        const [hours, minutes] = form.values.flightTime.split(':').map(Number);

        setFlight({
          __typename: 'FlightDetails',
          departure: {
            __typename: 'Departure',
            airport: airportCode,
            dateTime: {
              __typename: 'FlightDateTime',
              local: dayjs(form.values.departureDate)
                .hour(hours)
                .minute(minutes)
                .format(DATE_TIME_FLIGHT_FORMAT),
              utc: dayjs(form.values.departureDate)
                .hour(hours)
                .minute(minutes)
                .format(DATE_TIME_FLIGHT_FORMAT),
            },
          },
        });

        const { query } = router;
        router.push({
          pathname: '/check-slots',
          query: {
            ...query,
          },
        });
      } else if (form.values.departureDate) {
        fetchFlightInfo({
          variables: {
            flightDetails: {
              carrierCode: carrieCode,
              codeType: `${CODE_IATA}, ${CODE_ICAO}`,
              departureDate: formatDate(form.values.departureDate, DATE_FORMAT),
              flightNumber: flightNo,
              version: OAG_API_VERSION,
            },
          },
        });
      }

      values.flightNumber = upperCaseFlight;
    }

    setBooking(values);
  };

  return (
    <Layout
      bottomNavigation={
        <Button
          disabled={loadingProduct || checkAvailabilityLoading || viewOnly}
          onClick={() => form.onSubmit(handleClickCheckAvailability)()}
          type="submit"
        >
          {translations.booking.checkAvailability.btn}
        </Button>
      }
    >
      <form onSubmit={form.onSubmit(handleClickCheckAvailability)}>
        <Stack gap={8}>
          <Stack w="100%">
            <TopBarLinks page={pageName} />
          </Stack>
          <Flex align="center" className={classes.container}>
            <Stack className={classes.stack} gap={12}>
              {' '}
              <Center className={classes.titleWrapper}>
                <Heading as="h1" lineHeight={1} margin={0} padding={0}>
                  {mode === BOOKING_MODE.EDIT
                    ? translations.booking.flightAndGuests.amendTitle
                    : translations.booking.flightAndGuests.title}
                </Heading>
              </Center>
              <LoungeInfo />
              <DomNode ref={flightInfoRef}>
                <FlightInfo
                  flightNotFound={noFlightInfoError}
                  form={form}
                  loading={loadingProduct}
                  page={pageName}
                  productCategory={productCategory}
                  tags={[
                    mode === BOOKING_MODE.CREATE
                      ? ANALYTICS_TAGS.ON_CHANGE_DATE
                      : ANALYTICS_TAGS.ON_CHANGE_DATE_EDIT,
                    mode === BOOKING_MODE.CREATE
                      ? ANALYTICS_TAGS.ON_CHANGE_FLIGHT_NUMBER
                      : ANALYTICS_TAGS.ON_CHANGE_FLIGHT_NUMBER_EDIT,
                  ]}
                />
              </DomNode>
              <GuestInfo
                form={form}
                guestError={guestError}
                loading={loadingProduct}
                referreUrl={referrerUrl ?? '#'}
              />
              <EntitlementsBlock />
              <TotalPriceBlock showEntitlementsBlock={false} />
              <BookingAmendmentPayInFullInfoMessage />
            </Stack>
          </Flex>
        </Stack>
      </form>
    </Layout>
  );
};

export default CheckAvailability;
